import { createContext, ReactNode, useEffect, PropsWithChildren, useCallback } from 'react';

// project import
import defaultConfig, {
    LOGO_KEYS,
    KeysToAttributes,
    FAVICON_URL,
    COLOR_MODE,
    NavOptions,
    MENU_TYPE,
    NAV_TYPE,
    DRAWER_TYPE,
    PredefinedThemes,
    SECTIONS,
    ACTIONS
} from 'config';
import useLocalStorage from 'hooks/useLocalStorage';

// types
import { PaletteMode } from '@mui/material';
import { CustomizationProps } from 'types/config';
import { KeyedObject, LanguageOption } from 'types';
import { useRawAPI } from 'hooks/useApi';
import environment from 'environment';
import LAYOUT_CONST from 'constant';
import { calculateThemeColors } from 'utils/psbc';
import useI18n from 'i18n';

// initial state
const initialState: CustomizationProps = {
    ...defaultConfig,
    onChangeLayout: () => {},
    onChangeDrawer: () => {},
    onChangeMenuTypeOptions: () => {},
    onChangeMenuType: () => {},
    onChangeMode: () => {},
    onChangePresetColor: () => {},
    onChangeLocale: () => {},
    onChangeRTL: () => {},
    onChangeContainer: () => {},
    onChangeFontFamily: () => {},
    onChangeBorderRadius: () => {},
    onChangeOutlinedField: () => {}
};

// ==============================|| CONFIG CONTEXT & PROVIDER ||============================== //

const ConfigContext = createContext(initialState);

type ConfigProviderProps = {
    children: ReactNode;
};

function ConfigProvider({ children }: PropsWithChildren<{}>) {
    const api = useRawAPI();
    const [config, setConfig] = useLocalStorage('ifcp-settings', defaultConfig);
    const i18n = useI18n();
    useEffect(() => {
        // Fill new keys not present on local storage config.
        let key: keyof typeof defaultConfig;
        for (key in defaultConfig) {
            if (!Object.prototype.hasOwnProperty.call(config, key)) {
                config[key] = defaultConfig[key];
            }
        }

        api.get('admin/clients-dashboard-settings')
            .then((response: KeyedObject) => {
                if (response.data && response.data.config) {
                    let crmSettings: KeyedObject = response.data.config;
                    let crmConfig: KeyedObject = { ready: true };
                    LOGO_KEYS.forEach((item: string) => {
                        if (crmSettings[item] && crmSettings[item].file_id && crmSettings[item].url) {
                            let attr = KeysToAttributes[item];
                            crmConfig[attr] = crmSettings[item];
                            crmConfig[attr].url = environment.API_BASE_URL + crmSettings[item].url;
                        }
                    });

                    // Handle Color Mode
                    let colorModeUserPref = crmSettings[COLOR_MODE] == 'user_preference';
                    if (crmSettings[COLOR_MODE]) {
                        let attr = KeysToAttributes[COLOR_MODE];
                        if (colorModeUserPref) {
                            crmConfig[attr] = NavOptions;
                        } else {
                            crmConfig[attr] = [crmSettings[COLOR_MODE]];
                            crmConfig[NAV_TYPE] = crmSettings[COLOR_MODE];
                        }
                    }
                    // Handle Theme Colors
                    if (crmSettings.theme) {
                        let theme = crmSettings.theme;
                        if (theme.name && PredefinedThemes[theme.name]) {
                            theme = PredefinedThemes[theme.name];
                        } else {
                            theme = calculateThemeColors(theme.colors);
                        }

                        crmConfig.theme = theme;
                    }
                    // Handle Menu Type
                    if (crmSettings[MENU_TYPE]) {
                        let attr = KeysToAttributes[MENU_TYPE];
                        if (crmSettings[MENU_TYPE] == LAYOUT_CONST.VERTICAL_LAYOUT_COLLAPSED) {
                            // crmConfig[attr] = LAYOUT_CONST.VERTICAL_LAYOUT;
                            // crmConfig[DRAWER_TYPE] = LAYOUT_CONST.MINI_DRAWER;
                        } else {
                            crmConfig[attr] = crmSettings[MENU_TYPE];
                            crmConfig.layout = LAYOUT_CONST.VERTICAL_LAYOUT;
                            crmConfig[DRAWER_TYPE] = LAYOUT_CONST.DEFAULT_DRAWER;
                        }
                    }
                    // Specific for favicon
                    // let favicon: HTMLLinkElement =
                    //     (document.getElementById('favicon') as HTMLLinkElement) || document.createElement('link');
                    // favicon.rel = 'icon';
                    // if (crmSettings[FAVICON_URL] && crmSettings[FAVICON_URL].url) {
                    //     let faviconUrl: string = environment.API_BASE_URL + crmSettings[FAVICON_URL].url;
                    //     favicon.href = faviconUrl;
                    // }
                    // document.head.append(favicon);
                    // Enabled Sections
                    if (crmSettings[SECTIONS]) {
                        crmConfig[SECTIONS] = crmSettings[SECTIONS];
                    }
                    // Bar Actions
                    if (crmSettings[ACTIONS]) {
                        crmConfig[ACTIONS] = crmSettings[ACTIONS];
                    }

                    crmConfig.is_profile_editable = crmSettings?.is_profile_editable;
                    crmConfig.profile_editable_attributes = crmSettings?.profile_editable_attributes;
                    crmConfig.footer_message = crmSettings?.footer_message;
                    crmConfig.custom_js_code = crmSettings?.custom_js_code;
                    crmConfig.resources_head = crmSettings?.resources_head;
                    crmConfig.funds_accounts_allow_create = crmSettings?.funds_accounts_allow_create !== false;
                    crmConfig.funds_accounts_allow_edit = crmSettings?.funds_accounts_allow_edit !== false;
                    crmConfig.funds_accounts_allow_delete = crmSettings?.funds_accounts_allow_delete !== false;
                    crmConfig.bank_accounts_allow_create = crmSettings?.bank_accounts_allow_create !== false;
                    crmConfig.bank_accounts_allow_edit = crmSettings?.bank_accounts_allow_edit !== false;

                    setConfig({ ...config, ...crmConfig });
                }
            })
            .catch(console.error);
    }, []);

    const onChangeLayout = (layout: string) => {
        setConfig({
            ...config,
            layout
        });
    };

    const onChangeDrawer = (drawerType: string) => {
        setConfig({
            ...config,
            drawerType
        });
    };

    const onChangeMenuTypeOptions = (navTypeOptions: string[]) => {
        setConfig({
            ...config,
            navTypeOptions
        });
    };
    let onChangeMenuType = (navType: PaletteMode) => {
        setConfig({
            ...config,
            navType
        });
    };

    const onChangePresetColor = (presetColor: string) => {
        setConfig({
            ...config,
            presetColor
        });
    };

    let onChangeLocale = useCallback(
        (locale: LanguageOption) => {
            i18n.changeLanguage(locale.key);
            setConfig({
                ...config,
                locale: locale.key,
                rtlLayout: !!locale.rtl
            });
        },
        [config]
    );

    const onChangeRTL = (rtlLayout: boolean) => {
        setConfig({
            ...config,
            rtlLayout
        });
    };

    const onChangeContainer = () => {
        setConfig({
            ...config,
            container: !config.container
        });
    };

    const onChangeFontFamily = (fontFamily: string) => {
        setConfig({
            ...config,
            fontFamily
        });
    };

    const onChangeBorderRadius = (event: Event, newValue: number | number[]) => {
        setConfig({
            ...config,
            borderRadius: newValue as number
        });
    };

    const onChangeOutlinedField = (outlinedFilled: boolean) => {
        setConfig({
            ...config,
            outlinedFilled
        });
    };

    return (
        <ConfigContext.Provider
            value={{
                ...config,
                onChangeLayout,
                onChangeDrawer,
                onChangeMenuTypeOptions,
                onChangeMenuType,
                onChangePresetColor,
                onChangeLocale,
                onChangeRTL,
                onChangeContainer,
                onChangeFontFamily,
                onChangeBorderRadius,
                onChangeOutlinedField
            }}
        >
            {children}
        </ConfigContext.Provider>
    );
}

export { ConfigProvider, ConfigContext };
